import React, { useCallback, useEffect } from 'react';

import {
  authenticationStoreSelector,
  useAuthenticationStore,
} from '@repo/editor/src/stores/authenticationStore';
import {
  useUserStore,
  userStoreSelector,
} from '@repo/editor/src/stores/userStore';
import { withEventScope } from '@repo/event-processor';
import CreateIcon from '@repo/ui/components/Icon/CreateIcon';
import LoadingIcon from '@repo/ui/components/Icon/LoadingIcon';
import { Spacer } from '@repo/ui/components/Spacer';
import { H3 } from '@repo/ui/components/Typography';
import { themeStyle } from '@repo/ui/theming';

import NothingFoundIllustration from 'components/NothingFoundIllustration/NothingFoundIllustration';
import { FlexColumn } from 'styles/layouts';

import { MediaCard } from '../shared/MediaCard/MediaCard';

import { CenteredSpinnerWrapper, Grid } from './styles';
import { MockupsGroupGridProps } from './types';
import useMockupsGroupGrid from './useMockupsGroupGrid';

const MockupsGroupGrid: React.FC<MockupsGroupGridProps> = ({
  mockupsGroupId,
}) => {
  const { fetchMockups, mockups, isLoading } = useMockupsGroupGrid();
  const user = useUserStore(userStoreSelector.user);
  const openAuthentication = useAuthenticationStore(
    authenticationStoreSelector.openAuthentication
  );

  const handleOnSignUp = useCallback(
    (url: string, e: React.MouseEvent) => {
      if (!user) {
        e.preventDefault();
        openAuthentication({
          redirectUrl: url,
        });
      }
    },
    [user, openAuthentication]
  );

  useEffect(() => {
    fetchMockups(mockupsGroupId);
  }, [fetchMockups, mockupsGroupId]);

  if (isLoading) {
    return (
      <CenteredSpinnerWrapper
        theme={{ spinnerColor: themeStyle.varBrandMedium }}
      >
        <LoadingIcon height="50px" />
      </CenteredSpinnerWrapper>
    );
  }

  if (!mockups.length) {
    return (
      <FlexColumn alignItems="center" justifyContent="center">
        <NothingFoundIllustration />
        <Spacer height="12px" />
        <H3 color={themeStyle.varInkMedium}>No mockups found</H3>
      </FlexColumn>
    );
  }

  return (
    <Grid>
      {mockups.map((item) => (
        <MediaCard
          key={item.id}
          aspectRatio={1}
          buttonLabel="Use Mockup"
          height="100%"
          icon={<CreateIcon height="16px" />}
          image={item.preview}
          title=""
          url={item.url}
          onClick={(e: React.MouseEvent) => handleOnSignUp(item.url, e)}
        />
      ))}
    </Grid>
  );
};

export default withEventScope(MockupsGroupGrid, { module: 'mockupsGroupGrid' });
